(() => {
  if (customElements.get('ardg-similar-flight')) return;
  const numeric = value => {
    const text = String(value ?? '').trim();
    return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : null;
  };
  const shuffle = items => {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  };
  const pool = async (items, task) => {
    let index = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (index < items.length) await task(items[index++]);
    }));
  };
  class SimilarFlight extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-flight-config]').textContent);
      this.track = this.querySelector('[data-flight-track]');
      this.status = this.querySelector('[data-flight-status]');
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => {
        const amount = this.track.clientWidth * Number(button.dataset.direction);
        this.track.scrollBy({ left: amount, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.controller.signal }));
      this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true, signal: this.controller.signal });
      this.resizeObserver = new ResizeObserver(() => this.updateButtons());
      this.resizeObserver.observe(this.track);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this.load();
        }
      }, { rootMargin: '500px' });
      this.observer.observe(this);
      window.addEventListener('pageshow', event => {
        if (event.persisted && this.started) this.load(true);
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
    async fetchSection(path, page) {
      const url = new URL(path, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', this.config.section);
      if (page) url.searchParams.set(this.config.pageParam, page);
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Section request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!doc.querySelector('[data-flight-index]')) throw new Error('Section unavailable');
      return doc;
    }
    updateButtons() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-direction="-1"]').disabled = this.track.scrollLeft <= 1;
      this.querySelector('[data-direction="1"]').disabled = this.track.scrollLeft >= max - 1;
    }
    async load(restart = false) {
      if (this.started && !restart) return;
      this.started = true;
      this.hidden = false;
      this.track.replaceChildren();
      this.status.hidden = false;
      this.status.textContent = this.config.loading;
      this.setAttribute('aria-busy', 'true');
      try {
        const target = this.config.flight.map(numeric);
        if (target.some(value => value === null)) {
          this.status.textContent = this.config.missing;
          if (!Shopify.designMode) this.hidden = true;
          return;
        }
        if (!this.catalog) {
          const records = JSON.parse(this.querySelector('[data-flight-index]').textContent);
          const pages = Array.from({ length: this.config.pages - 1 }, (_, i) => i + 2);
          await pool(pages, async page => {
            const doc = await this.fetchSection(this.config.url, page);
            records.push(...JSON.parse(doc.querySelector('[data-flight-index]').textContent));
          });
          this.catalog = [...new Map(records.map(item => [item.id, item])).values()];
        }
        const tolerances = this.config.mode === 'exact' ? [0, 0, 0, 0] : this.config.tolerance;
        const matches = shuffle(this.catalog.filter(item => {
          if (item.id === this.config.product) return false;
          const flight = item.flight.map(numeric);
          return flight.every((value, i) => value !== null && Math.abs(value - target[i]) <= tolerances[i] + 1e-9);
        }));
        const selected = matches.slice(0, this.config.limit);
        const cards = new Array(selected.length);
        await pool(selected.map((item, index) => ({ item, index })), async ({ item, index }) => {
          const doc = await this.fetchSection(item.url);
          const template = doc.querySelector('[data-flight-card]');
          if (!template?.content.firstElementChild) throw new Error('Card unavailable');
          cards[index] = document.importNode(template.content, true);
        });
        cards.forEach(card => this.track.append(card));
        this.status.hidden = cards.length > 0;
        this.status.textContent = this.config.empty;
        if (!cards.length && !Shopify.designMode) this.hidden = true;
        this.updateButtons();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = this.config.error;
          if (!Shopify.designMode) this.hidden = true;
          console.warn('Similar flight carousel:', error);
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('ardg-similar-flight', SimilarFlight);
})();
